import { isNumber } from 'lodash';

import { type ConflictDictionary, type ConflictKey } from '@neptune/charts-domain';
import { type Widget } from '@neptune/shared/widgets-domain';

import { ChartGlobalConfig } from './chart-widget-global-configuration';

import { ChartWidgetOptions } from './chart-widget';

export function getChartConflictKeys(
  config?: ChartGlobalConfig,
  options?: ChartWidgetOptions,
): ConflictKey[] {
  if (!config || !options) {
    return [];
  }

  const conflicts: ConflictKey[] = [];

  if (
    (options.xAxisMetric &&
      config.xAxisMetric &&
      options.xAxisMetric.name !== config.xAxisMetric.name) ||
    (options.xAxisMode && config.xAxisMode && options.xAxisMode !== config.xAxisMode)
  ) {
    conflicts.push('xAxisMode');
  }

  if (
    (options.xAxisScale && config.xAxisScale && options.xAxisScale !== config.xAxisScale) ||
    (isNumber(options.xAxisRange?.[0]) &&
      isNumber(config.xAxisRange?.[0]) &&
      options.xAxisRange?.[0] !== config.xAxisRange?.[0]) ||
    (isNumber(options.xAxisRange?.[1]) &&
      isNumber(config.xAxisRange?.[1]) &&
      options.xAxisRange?.[1] !== config.xAxisRange?.[1])
  ) {
    conflicts.push('xAxis');
  }

  if (options.yAxisScale && config.yAxisScale && options.yAxisScale !== config.yAxisScale) {
    conflicts.push('yAxis');
  }

  if (conflicts.includes('xAxis') || conflicts.includes('yAxis')) {
    conflicts.push('axis');
  }

  if (
    (isNumber(options.smoothing) &&
      isNumber(config.smoothing) &&
      options.smoothing !== config.smoothing) ||
    (options.smoothingAlgorithm &&
      config.smoothingAlgorithm &&
      options.smoothingAlgorithm !== config.smoothingAlgorithm) ||
    (options.showOriginalLineOnSmoothing !== undefined &&
      config.showOriginalLineOnSmoothing !== undefined &&
      options.showOriginalLineOnSmoothing !== config.showOriginalLineOnSmoothing)
  ) {
    conflicts.push('smoothing');
  }

  if (
    options.outliersDisplayMode !== undefined &&
    config.outliersDisplayMode !== undefined &&
    options.outliersDisplayMode !== config.outliersDisplayMode
  ) {
    conflicts.push('outliersDisplayMode');
  }

  if (
    options.showCompletion !== undefined &&
    config.showCompletion !== undefined &&
    options.showCompletion !== config.showCompletion
  ) {
    conflicts.push('showCompletion');
  }

  return conflicts;
}

export function findChartOptionsConflicts<TWidget extends Widget>({
  globalConfig,
  widgets,
  getChartOptionsFromWidget,
}: {
  globalConfig?: ChartGlobalConfig;
  widgets?: TWidget[];
  getChartOptionsFromWidget: (widget: TWidget) => ChartWidgetOptions | undefined;
}): ConflictDictionary {
  if (!widgets || !globalConfig) {
    return {};
  }

  const conflicts: ConflictDictionary = {};

  for (const widget of widgets) {
    const chartOptions = getChartOptionsFromWidget(widget);

    if (chartOptions) {
      for (const key of getChartConflictKeys(globalConfig, chartOptions)) {
        (conflicts[key] ??= []).push(widget.id);
      }
    }
  }

  return conflicts;
}

export function resetWidgetsChartOptionsForConflicts<TWidget extends Widget>({
  widgets,
  key,
  ids,
  getChartOptionsFromWidget,
  setChartOptionsInWidget,
}: {
  widgets: TWidget[];
  key: ConflictKey;
  ids?: string[];
  getChartOptionsFromWidget: (widget: TWidget) => ChartWidgetOptions | undefined;
  setChartOptionsInWidget: (widget: TWidget, options: ChartWidgetOptions) => TWidget;
}): TWidget[] {
  if (!ids) {
    return widgets;
  }

  return widgets.map((widget) => {
    if (!ids.includes(widget.id)) {
      return widget;
    }

    const chartOptions = getChartOptionsFromWidget(widget);

    if (!chartOptions) {
      return widget;
    }

    const newChartOptions = resetChartOptionsForConflict(chartOptions, key);

    return setChartOptionsInWidget(widget, newChartOptions);
  });
}

function resetChartOptionsForConflict(
  options: ChartWidgetOptions,
  key: ConflictKey,
): ChartWidgetOptions {
  return {
    ...options,
    ...(key === 'xAxisMode'
      ? {
          xAxisMode: undefined,
          xAxisMetric: undefined,
          customXExpression: undefined,
          customXExpressionName: undefined,
        }
      : {}),
    ...(key === 'xAxis' || key === 'axis' ? { xAxisScale: undefined, xAxisRange: undefined } : {}),
    ...(key === 'yAxis' || key === 'axis' ? { yAxisScale: undefined } : {}),
    ...(key === 'smoothing'
      ? {
          smoothing: undefined,
          smoothingAlgorithm: undefined,
          showOriginalLineOnSmoothing: undefined,
        }
      : {}),
    ...(key === 'outliersDisplayMode' ? { outliersDisplayMode: undefined } : {}),
    ...(key === 'showCompletion' ? { showCompletion: undefined } : {}),
  };
}
